import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DataTableColumn,
    IFilterChip,
    PaginatedProps,
    StandardListViewV2,
    UserPermissionContext,
    useFailedCreateSnackbar,
    useSuccessfulCreateSnackbar,
} from '../../Components/CoreLib/library';
import { triaxReferenceNumberColumnKeySuffixes } from '../../constants/triaxReferenceNumberColumnKeySuffixes';
import { JobDto, OrderDto } from '../../dtos';
import {
    useArchiveOrderBulkDeleteMutation,
    useCreateOrderDownloadSelectedOrdersMutation,
    useCreateOrderMutation,
    useGetOrderQuery,
    useOrderBulkArchiveMutation,
    useOrderBulkUnarchiveMutation,
} from '../../store/generated/generatedApi';
import { Link, Typography } from '@mui/material';
import { AddOrderDateModal } from './AddOrderDateModal';
import { OrderFilterDialog } from './Components/OrderFilterDialog';
import { useOrderListFilters } from './Utils/useOrderListFilters';
import { emptyGuid, exportToPdf, formatDate } from '../../util';
import { Download } from '@mui/icons-material';

export interface IOrdersListViewProps {
    job: JobDto;
}

const defaultPaginationProps: PaginatedProps = {
    sorting: [{ key: 'ORDER_DATE', direction: 'desc' }, { key: 'ORDER_NUMBER', direction: 'desc' }],
    page: 0,
    pageSize: 25,
};

const ORDER_FILTER_CHIP_KEYS = {
    material: 'Material',
    orderDate: 'Order date',
} as const;

export const OrderListView: FC<IOrdersListViewProps> = (props) => {
    const { job } = props;
    const navigate = useNavigate();
    const { permissions } = useContext(UserPermissionContext);
    const [createOrder, { data, isError, isSuccess, reset }] = useCreateOrderMutation();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const { orderFilters, setOrderFilters } = useOrderListFilters({ includeCustomer: false });
    const [isFilterDialogVisible, setIsFilterDialogVisible] = useState(false);
    const [downloadSelectedPdfs, { isLoading: isDownloadingPdfs }] = useCreateOrderDownloadSelectedOrdersMutation();

    const additionalQueryParameters = useMemo(() => {
        if (!job.currentQuote?.id) {
            return {};
        }
        const params: Record<string, string> = {
            quoteId: job.currentQuote.id,
        };
        if (orderFilters.materialId !== emptyGuid) {
            params.materialId = orderFilters.materialId;
        }
        if (orderFilters.orderDate) {
            params.orderDate = orderFilters.orderDate.toISOString();
        }
        return params;
    }, [job.currentQuote, orderFilters]);

    const filterChips = useMemo<IFilterChip[]>(() => {
        const chips: IFilterChip[] = [];
        if (orderFilters.materialId !== emptyGuid && orderFilters.materialLabel) {
            chips.push({ key: ORDER_FILTER_CHIP_KEYS.material, text: orderFilters.materialLabel });
        }
        if (orderFilters.orderDate) {
            chips.push({ key: ORDER_FILTER_CHIP_KEYS.orderDate, text: formatDate(orderFilters.orderDate) });
        }
        return chips;
    }, [orderFilters]);

    const handleFilterChipDelete = useCallback((filterChipKey: string) => {
        setOrderFilters((prev) => {
            switch (filterChipKey) {
                case ORDER_FILTER_CHIP_KEYS.material:
                    return { ...prev, materialId: emptyGuid, materialLabel: '' };
                case ORDER_FILTER_CHIP_KEYS.orderDate:
                    return { ...prev, orderDate: undefined };
                default:
                    return prev;
            }
        });
    }, [setOrderFilters]);

    useSuccessfulCreateSnackbar('Order', isSuccess, () => {
        navigate(`/job/${job.id}/order/${data?.id}`)
    });

    useFailedCreateSnackbar('order', isError, reset)

    const handleSave = (values: OrderDto) => {
        createOrder(values);
    };

    const handleCancel = () => {
        setIsAddModalOpen(false);
    };

    const tableColumns: DataTableColumn<OrderDto>[] = useMemo(
        () => [
            { key: 'orderNumber', label: 'Order #', sortKey: 'ORDER_NUMBER' },
            {
                key: 'orderDate',
                label: 'Order Date',
                sortKey: 'ORDER_DATE',
                fieldMapper: (row) => (row.orderDate ? formatDate(row.orderDate) : ''),
            },
            { key: 'orderer', label: 'Orderer', sortKey: 'ORDERER', fieldMapper: (row) => row.orderer || '' },
            { key: 'orderLineItemCount', label: 'Line Items Count', sortKey: 'LINE_ITEM_COUNT' },
            {
                key: 'quoteNumber',
                label: job.currentQuote?.appraisalType === 0 ? 'Quote #' : 'Estimate #',
                sortKey: 'QUOTE_NUMBER',
                fieldMapper: (row) => (
                    <Typography>
                        {permissions.includes('edit:quote') ? (
                            <Link
                                href={`/quote/${row.quoteId}`}
                                variant='body2'
                                onClick={() => {
                                    navigate(`/quote/${row.quoteId}`);
                                }}>
                                {row.quote?.quoteNumber}
                            </Link>
                        ) : (
                            <>{row.quote?.quoteNumber}</>
                        )}
                    </Typography>
                ),
            },
            {
                key: 'status', label: 'Status', sortKey: 'STATUS',
                fieldMapper: (row) => (
                    <Typography>
                        {!!row.orderReviewId ? (
                            <Link
                                href={`/job/${row.jobId}/order/${row.id}/orderReview/${row.orderReviewId}`}
                                variant='body2'
                                onClick={() => {
                                    navigate(`/job/${row.jobId}/order/${row.id}/orderReview/${row.orderReviewId}`);
                                }}>
                                {row.status}
                            </Link>
                        ) : (
                            <>{row.status}</>
                        )}
                    </Typography>
                ),
                unsorted: true,
            },
            { key: 'createdOn', label: 'Created On', sortKey: 'CREATED_ON', fieldMapper: (row) => (row.createdOn ? formatDate(row.createdOn, 'Pp') : '') },
        ],
        [navigate, permissions, job.currentQuote?.appraisalType]
    );

    const handleCreateNew = useCallback(() => {
        setIsAddModalOpen(true);
    }, []);

    const handleTableEdit = useCallback((id: string) => {
        navigate(`/job/${job.id}/order/${id}`);
    }, [job.id, navigate]);

    const handleDownload = useCallback((selectedItems: OrderDto[]) => {
        if (selectedItems.length > 0) {
            const selectedIds = selectedItems.map(x => x.id);
            downloadSelectedPdfs({
                orderIds: selectedIds,
            }).then((response: any) => {
                if (response.data) {
                    exportToPdf(response.data, `Orders.pdf`.replace(/\s+/g, ''));
                }
            })
        }
    }, [downloadSelectedPdfs]);

    return (
        <>
            <OrderFilterDialog
                isVisible={isFilterDialogVisible}
                onClose={() => setIsFilterDialogVisible(false)}
                filterProps={orderFilters}
                setFilterProps={setOrderFilters}
                showCustomerFilter={false}
            />
            {isAddModalOpen && <AddOrderDateModal onSave={handleSave} onClose={handleCancel} open={isAddModalOpen} job={job} />}
            {job.currentQuote !== null && (
                <StandardListViewV2
                    referenceNumberColumnKeySuffixes={triaxReferenceNumberColumnKeySuffixes}
                    enableMultiSort
                    headerIcon={<></>}
                    headerTitle=''
                    breadCrumbs={[]}
                    defaultPaginationProps={defaultPaginationProps}
                    getDataQuery={useGetOrderQuery}
                    isShowActiveToggleVisible
                    inactivateMutation={useOrderBulkArchiveMutation}
                    activateMutation={useOrderBulkUnarchiveMutation}
                    deleteMutation={useArchiveOrderBulkDeleteMutation}
                    tableColumns={tableColumns}
                    entityNameSingular='Order'
                    permissionName='order'
                    handleAdd={handleCreateNew}
                    handleEdit={handleTableEdit}
                    disableDeleteForRecord={(record: OrderDto) => !!record.orderReview}
                    disableInactiveForRecord={(record: OrderDto) => !!record.orderReview}
                    additionalQueryParameters={additionalQueryParameters}
                    handleFilterClicked={() => setIsFilterDialogVisible(true)}
                    filterChips={filterChips}
                    handleFilterChipDelete={handleFilterChipDelete}
                    additionalActionItems={[
                        {
                            text: 'DOWNLOAD',
                            onClick: (selectedItems: OrderDto[]) => {
                                handleDownload(selectedItems);
                            },
                            icon: <Download />,
                            enabledCheck: (selected: OrderDto[]) => selected.length > 0 && !isDownloadingPdfs,
                            clearSelectedItems: true,
                        }
                    ]}
                    paginationOnTop={true}
                    stickyTableHeader={true}
                />
            )}
        </>
    );
};
